import { OfflineDatabase } from "./database";
import { PreOrderStore } from "./stores/preOrderStore";
import { SyncQueueStore } from "./stores/syncQueueStore";
import { PartnerStore } from "./stores/partnerStore";
import { ChatSessionStore } from "./stores/chatSessionStore";
import { ChatMessageStore } from "./stores/chatMessageStore";
import { ChatExecutionStore } from "./stores/chatExecutionStore";

export { ChatSessionStore } from "./stores/chatSessionStore";
export { ChatMessageStore } from "./stores/chatMessageStore";
export { ChatExecutionStore } from "./stores/chatExecutionStore";
import { ChangeTracker } from "./sync/changeTracker";
import { BatchProcessor } from "./sync/batchProcessor";
import {
  PreOrderNumberManager,
  DEFAULT_MOD,
  DEFAULT_OFFSET,
} from "./preOrderNumberManager";

// Re-export classes
export {
  OfflineDatabase,
  PreOrderStore,
  SyncQueueStore,
  PartnerStore,
  ChangeTracker,
  BatchProcessor,
  PreOrderNumberManager,
  DEFAULT_MOD,
  DEFAULT_OFFSET,
};

// Re-export NetworkInterceptor
export { getNetworkInterceptor } from "./sync/networkInterceptor";

// Re-export types
export type {
  CachedPreOrder,
  CachedPartner,
  CachedChatSession,
  CachedChatMessage,
  CachedChatExecution,
  SyncChange,
  OfflineDBMetadata,
} from "./schema";

export { DB_NAME, DB_VERSION, STORES } from "./schema";

export type {
  BatchValidateRequest,
  BatchValidateResponse,
  SyncResult,
} from "./sync/batchProcessor";

// Singleton instances
let dbInstance: OfflineDatabase | null = null;
let preOrderStoreInstance: PreOrderStore | null = null;
let syncQueueStoreInstance: SyncQueueStore | null = null;
let partnerStoreInstance: PartnerStore | null = null;
let changeTrackerInstance: ChangeTracker | null = null;
let batchProcessorInstance: BatchProcessor | null = null;
let chatSessionStoreInstance: ChatSessionStore | null = null;
let chatMessageStoreInstance: ChatMessageStore | null = null;
let chatExecutionStoreInstance: ChatExecutionStore | null = null;

export function getOfflineDatabase(): OfflineDatabase {
  if (!dbInstance) {
    dbInstance = new OfflineDatabase();
  }
  return dbInstance;
}

/**
 * Close the legacy offline DB's IndexedDB connection. Call before wiping
 * storage on logout / user-switch: the singleton holds `stockline-offline-db`
 * open for the tab's lifetime, which blocks `indexedDB.deleteDatabase` — a
 * silent no-op wipe on Chromium and a hang (white screen) on Firefox. The
 * stores re-open lazily on next use.
 */
export function closeOfflineDatabase(): void {
  dbInstance?.close();
}

export function getPreOrderStore(): PreOrderStore {
  if (!preOrderStoreInstance) {
    preOrderStoreInstance = new PreOrderStore(getOfflineDatabase());
  }
  return preOrderStoreInstance;
}

export function getSyncQueueStore(): SyncQueueStore {
  if (!syncQueueStoreInstance) {
    syncQueueStoreInstance = new SyncQueueStore(getOfflineDatabase());
  }
  return syncQueueStoreInstance;
}

export function getPartnerStore(): PartnerStore {
  if (!partnerStoreInstance) {
    partnerStoreInstance = new PartnerStore(getOfflineDatabase());
  }
  return partnerStoreInstance;
}

export function getChangeTracker(): ChangeTracker {
  if (!changeTrackerInstance) {
    changeTrackerInstance = new ChangeTracker(getSyncQueueStore());
  }
  return changeTrackerInstance;
}

export function getBatchProcessor(): BatchProcessor {
  if (!batchProcessorInstance) {
    batchProcessorInstance = new BatchProcessor(
      getChangeTracker(),
      getSyncQueueStore(),
      getPreOrderStore(),
    );
  }
  return batchProcessorInstance;
}

export function getChatSessionStore(): ChatSessionStore {
  chatSessionStoreInstance ??= new ChatSessionStore(getOfflineDatabase());
  return chatSessionStoreInstance;
}

export function getChatMessageStore(): ChatMessageStore {
  chatMessageStoreInstance ??= new ChatMessageStore(getOfflineDatabase());
  return chatMessageStoreInstance;
}

export function getChatExecutionStore(): ChatExecutionStore {
  chatExecutionStoreInstance ??= new ChatExecutionStore(getOfflineDatabase());
  return chatExecutionStoreInstance;
}

/**
 * Get or create the PreOrderNumberManager singleton.
 *
 * @param mod - Company-wide mod value from feature flags (default: 10)
 * @param offset - User-specific offset from feature flags (default: 0)
 * @param companyId - Current company ID (required for proper scoping)
 * @param userId - Current user ID (required for proper scoping)
 *
 * Note: If called with different params than existing instance,
 * the existing instance is destroyed and a new one is created.
 */
export function getPreOrderNumberManager(
  mod: number,
  offset: number,
  companyId: string,
  userId: string,
): PreOrderNumberManager {
  return PreOrderNumberManager.getInstance(mod, offset, companyId, userId);
}

/**
 * Reset the PreOrderNumberManager singleton.
 * Use this when user logs out or company changes.
 */
export function resetPreOrderNumberManager(): void {
  const instance = PreOrderNumberManager.getExistingInstance();
  if (instance) {
    instance.destroy();
  }
}
